/**
 * Convert a number from one unit to another
 *
 * @param {number} x - value to convert from one unit to another
 * @param {string} from - current unit (string) of x
 * @param {string} to - desired unit (string) for conversion
 * @param {number} [precision] - 'int' if you want the output rounded to the nearest integer
 * @param {boolean} [throwError] - if true, throw an error if the unit is not recognized
 * @returns
 */
export function Convert(x, from, to, precision, throwError) {
  let resolveAlias = function (unit) {
    let compare = unit.toLowerCase().replace(/\s+/, '_');
    // Check for any aliases and return proper unit strings
    switch (compare) {
      case 'lb':
        return 'lbf';
      case 'lb/ft':
        return 'lbf/ft';
      case 'lb/ft^2':
        return 'lbf/ft^2';
      case 'lb/ft^3':
        return 'lbf/ft^3';
      case 'n/m^2':
        return 'Pa';
      case 'celsius':
        return 'C';
      case 'centimetre':
        return 'cm';
      // case 'centimetre_to_the_fourth': return '';
      // case 'cubic_centimetre': return '';
      // case 'cubic_inch': return '';
      // case 'cubic_foot': return '';
      case 'fahrenheit':
        return 'F';
      // case 'kilogram': return '';
      case 'metre':
        return 'm';
      case 'pascal':
        return 'Pa';
      case 'square_metre':
        return 'm^2';
      // case 'cubic_metre': return '';
      // case 'metre_to_the_fourth': return '';
      case 'newton':
        return 'N';
      // case 'kilometre_per_hour': return '';
      case 'kilogram_per_cubic_metre':
        return 'kg/m^3';
      // case 'kilogram_per_metre': return '';
      case 'newton_per_metre':
        return 'N/m';
      // case 'metre_per_square_second': return '';
      // case 'newton_metre': return '';
      // case 'percent': return '';
      // case 'ratio': return '';
      // case 'safety_factor': return '';
      case 'degree_angle':
        return 'deg';
      case 'radian':
        return 'rad';
      // case 'gram': return '';
      // case 'pound': return '';
      case 'millimetre':
        return 'mm';
      case 'foot':
        return 'ft';
      case 'inch':
        return 'in';
      case 'mile':
        return 'mi';
      // case 'quarter_inch': return '';
      // case 'sixteenth_inch': return '';
      // case 'tenth_inch': return '';
      // case 'kilo_pascal': return '';
      // case 'inch_to_the_fourth': return '';
      // case 'millimetre_to_the_fourth': return '';
      case 'square_inch':
        return 'in^2';
      case 'square_foot':
        return 'ft^2';
      case 'square_centimetre':
        return 'cm^2';
      case 'square_millimetre':
        return 'mm^2';
      // case 'pound_force_per_square_inch': return '';
      case 'pound_force_per_square_foot':
        return 'lbf/ft^2';
      case 'pound_force':
        return 'lbf';
      // case 'mile_per_hour': return '';
      case 'pound_per_cubic_foot':
        return 'lbf/ft^3';
      case 'pound_per_foot':
        return 'lbf/ft'; //todo is this the same as lbf/ft?
      case 'pound_force_per_foot':
        return 'lbf/ft';
      // case 'pound_force_inch': return '';
      // case 'pound_force_foot': return '';
      case 'per_celsius':
        return 'C^-1';
      case 'per_fahrenheit':
        return 'F^-1';
      // case 'volt': return '';
      // case 'metre_per_second': return '';
      // case 'feet_per_second': return '';
    }

    // No alias, return the unit string as is
    return unit;
  };

  let standardConversion = function (x, unit, toStandard) {
    // Are we converting to or from standard (assign y: 1 if converting to, -1 if converting from standard)
    let y = toStandard ? 1 : -1;

    // Do the proper conversion based on whether we are converting to or from standard
    switch (unit) {
      // Lengths (standard: m)
      case 'm':
        return x;
      case 'in':
        return x * 0.0254 ** y;
      case 'ft':
        return x * 0.3048 ** y;
      case 'mi':
        return x * 1609.34 ** y;
      case 'mm':
        return x * 0.001 ** y;
      case 'cm':
        return x * 0.01 ** y;
      case 'km':
        return x * 1000 ** y;

      // Area (standard: m^2)
      case 'm^2':
        return x;
      case 'in^2':
        return x * 0.00064516 ** y;
      case 'ft^2':
        return x * 0.092903 ** y;
      case 'mi^2':
        return x * 2589988 ** y;
      case 'mm^2':
        return x * 0.000001 ** y;
      case 'cm^2':
        return x * 0.0001 ** y;
      case 'km^2':
        return x * 1000000 ** y;

      // Volume (standard: m^3)
      case 'm^3':
        return x;
      case 'cm^3':
        return x * 1e-6 ** y;
      case 'ft^3':
        return x * 0.0283168 ** y;
      case 'in^3':
        return x * 1.6387e-5 ** y;

      // Force (standard: N)
      case 'N':
        return x;
      case 'lbf':
        return x * 4.44822 ** y;

      // Mass (standard: kg)
      case 'kg':
        return x;
      case 'g':
        return x * 1e-3 ** y;
      case 'lbm':
        return x * 0.453592 ** y;
      case 'slug':
        return x * 14.5939 ** y;

      // Linear density (standard: N/m)
      case 'N/m':
        return x;
      case 'kg/m':
        return x * 9.80655 ** y;
      case 'lbf/ft':
        return x * 14.593899 ** y;
      case 'lbf/in':
        return x * 175.12679 ** y;

      // Pressure (standard: Pa)
      case 'Pa':
        return x;
      case 'kPa':
        return x * 1e3 ** y;
      case 'psi':
        return x * 6894.76 ** y;
      case 'lbf/ft^2':
        return x * 47.88 ** y;

      // Density (standard: N/m^3)
      case 'N/m^3':
        return x;
      case 'kg/m^3':
        return x * 9.80655 ** y;
      case 'lbf/ft^3':
        return x * 157.09 ** y;

      // Angle (standard: rad)
      case 'rad':
        return x;
      case 'deg':
        return x * (Math.PI / 180) ** y;

      // Temperature (standard: C)
      case 'C':
        return x;
      case 'F':
        return y == 1 ? ((x - 32) * 5) / 9 : (x * 9) / 5 + 32;

      // Temperature Coefficient (standard: C^-1)
      case 'C^-1':
        return x;
      case 'F^-1':
        return x * 1.8 ** y;

      // Time (standard: mins)
      case 'mins':
        return x;
      case 'millis':
        return x * (1 / 60000) ** y;
      case 'secs':
        return x * (1 / 60) ** y;
      case 'hrs':
        return x * 60 ** y;
      case 'days':
        return x * 1440 ** y;

      // Moment
      case 'N*m':
        return x;
      case 'lbf*ft':
        return x * 1.35582 ** y;
    }

    // If we didn't return, the units we are using are unrecognized; warn the user
    console.error('Unrecognized unit:', unit);
    if (throwError) throw new Error(`Unrecognized unit: ${unit}`);
    return x;
  };

  // Standardize incoming data
  x = parseFloat(x);
  from = resolveAlias(from);
  to = resolveAlias(to);

  // Cconvert value to a standard unit first
  x = standardConversion(x, from, true);
  // Then convert from standard to desired units
  x = standardConversion(x, to, false);

  // If precision is "int", parse x to an integer
  if (precision == 'int') x = parseInt(x);
  // If precision is numeric, cast to a string
  else if (precision != undefined) x = x.toFixed(precision);
  // Finally, return x
  return x;
}
